import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from "date-fns";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";

function normalizeListPayload(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  return [];
}

function toYmdDate(input) {
  if (input == null) return "";
  const s = String(input).trim();
  if (!s) return "";
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return format(d, "yyyy-MM-dd");
}

function formatDateLabel(input) {
  const ymd = toYmdDate(input);
  if (!ymd) return "—";
  try {
    return format(parseISO(`${ymd}T12:00:00`), "d MMM yyyy");
  } catch {
    return ymd;
  }
}

function statusChipColor(status) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "present") return "success";
  if (s === "absent") return "error";
  return "default";
}

function oneLine(t) {
  return String(t ?? "")
    .replace(/[\r\n\t]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function safeFilePart(s) {
  return String(s || "student")
    .replace(/[^\w\-]+/g, "_")
    .replace(/_+/g, "_")
    .slice(0, 40);
}

function trainerDisplayName(trainer) {
  if (!trainer) return "";
  const appUsers =
    trainer.Applicationuser ||
    trainer.applicationuser ||
    trainer.application_user;
  const first = Array.isArray(appUsers) ? appUsers[0] : appUsers;
  return String(first?.full_name || trainer?.full_name || "").trim();
}

function normalizeRecordStatus(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const lower = s.toLowerCase();
  if (lower === "scheduled") return "Scheduled";
  if (lower === "ongoing") return "Ongoing";
  if (lower === "completed" || lower === "conducted") return "Completed";
  if (lower === "cancelled" || lower === "canceled") return "Cancelled";
  return s;
}

const RECORD_STATUS_COLORS = {
  Scheduled: "#BBDEFB",
  Ongoing: "#B2EBF2",
  Completed: "#C8E6C9",
  Cancelled: "#FFCDD2",
};

const ATTENDANCE_COLORS = {
  Present: "#C8E6C9",
  Absent: "#FFCDD2",
  "Not marked": "#FFF59D", // amber/yellow — distinct from No class grey
};

function shortAttendanceMark(status) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "present") return "P";
  if (s === "absent") return "A";
  return "·";
}

function attendanceBgColor(status) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "present") return ATTENDANCE_COLORS.Present;
  if (s === "absent") return ATTENDANCE_COLORS.Absent;
  if (s.includes("not") || s === "unmarked" || !s) {
    return ATTENDANCE_COLORS["Not marked"];
  }
  return ATTENDANCE_COLORS["Not marked"];
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const FULL_WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const NO_CLASS_BG = "#d9d9d9";
const CLASS_DAY_EMPTY_BG = "#F5F5F5";

function normalizeBatchDays(raw) {
  if (Array.isArray(raw)) {
    return raw.map((d) => String(d || "").trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((d) => String(d || "").trim()).filter(Boolean);
      }
    } catch {
      /* comma list */
    }
    return raw
      .split(",")
      .map((d) => String(d || "").trim())
      .filter(Boolean);
  }
  return [];
}

function sortBatchDays(days) {
  const order = FULL_WEEKDAY_NAMES.map((d) => d.toLowerCase());
  return [...(days || [])].sort((a, b) => {
    const ia = order.indexOf(String(a || "").trim().toLowerCase());
    const ib = order.indexOf(String(b || "").trim().toLowerCase());
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
}

function weekdayName(day) {
  return FULL_WEEKDAY_NAMES[day.getDay()] || "";
}

/**
 * Calendar of one student's attendance across all class records / batches.
 * @param {boolean} [embedded] When true, render inline (no modal dialog).
 */
export default function StudentAttendanceViewDialog({
  open,
  studentId,
  studentName = "",
  studentCode = "",
  onClose,
  embedded = false,
}) {
  const { userData } = useAuthStore();
  const orgId =
    userData?.organization?.organization_id ||
    userData?.organization_id ||
    localStorage.getItem("organization_id");
  const organizationName =
    userData?.organization?.organization_name ||
    userData?.organization?.name ||
    "";
  const token = localStorage.getItem("token");
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [sessions, setSessions] = useState([]);
  /** @type {Array<{ batch_id: number, batch_label: string, days: string[], start_ymd: string, end_ymd: string }>} */
  const [batchSchedules, setBatchSchedules] = useState([]);
  const [batchDaysLabel, setBatchDaysLabel] = useState("");
  const [viewMode, setViewMode] = useState("month");
  const [panelMode, setPanelMode] = useState("calendar");
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedYmd, setSelectedYmd] = useState(null);
  const [downloadAnchor, setDownloadAnchor] = useState(null);

  const load = useCallback(async () => {
    if (!open || !studentId || !orgId) return;
    setLoading(true);
    setErrorText("");
    setSessions([]);
    setBatchSchedules([]);
    setBatchDaysLabel("");
    setSelectedYmd(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const baseParams = { organization_id: orgId, per_page: "all" };

      const [attRes, recordsRes, bsRes, batchesRes] = await Promise.all([
        axios.get(`${MAIN_URL}/api/ems/classes-attendance`, {
          headers,
          params: {
            ...baseParams,
            organization_ems_student_id: studentId,
          },
        }),
        axios.get(`${MAIN_URL}/api/ems/class-records`, {
          headers,
          params: baseParams,
        }),
        axios.get(`${MAIN_URL}/api/ems/batch-students`, {
          headers,
          params: baseParams,
        }),
        axios.get(`${MAIN_URL}/api/ems/batches`, {
          headers,
          params: baseParams,
        }),
      ]);

      const batchesById = new Map();
      normalizeListPayload(batchesRes.data?.data ?? batchesRes.data).forEach(
        (b) => {
          const id = Number(b?.organization_ems_batch_id);
          if (id) batchesById.set(id, b);
        }
      );

      const studentBatches = normalizeListPayload(
        bsRes.data?.data ?? bsRes.data
      ).filter(
        (r) =>
          Number(
            r?.organization_ems_student_id ??
              r?.student?.organization_ems_student_id
          ) === Number(studentId)
      );

      const schedules = [];
      const unionDays = new Set();
      studentBatches.forEach((bs) => {
        const batchId = Number(
          bs?.organization_ems_batch_id ??
            bs?.batch?.organization_ems_batch_id
        );
        if (!batchId) return;
        const batch = batchesById.get(batchId) || bs?.batch || {};
        const days = normalizeBatchDays(batch?.batch_days);
        days.forEach((d) => unionDays.add(d));
        // Student enrollment window: enrollment_date → completion_date (fallback batch dates)
        const startYmd =
          toYmdDate(bs?.enrollment_date) ||
          toYmdDate(batch?.start_date);
        const endYmd =
          toYmdDate(bs?.completion_date) ||
          toYmdDate(bs?.completed_date) ||
          toYmdDate(batch?.end_date);
        schedules.push({
          batch_id: batchId,
          batch_label:
            batch?.batch_code ||
            batch?.batch_name ||
            `Batch #${batchId}`,
          days,
          start_ymd: startYmd,
          end_ymd: endYmd,
        });
      });
      setBatchSchedules(schedules);
      setBatchDaysLabel(sortBatchDays([...unionDays]).join(", "));

      let attendanceRaw = normalizeListPayload(
        attRes.data?.data ?? attRes.data
      ).filter(
        (a) => Number(a?.organization_ems_student_id) === Number(studentId)
      );

      const recordsRaw = normalizeListPayload(
        recordsRes.data?.data ?? recordsRes.data
      );
      const recordById = new Map();
      recordsRaw.forEach((r) => {
        const id = Number(r?.organization_ems_batch_class_record_id);
        if (id) recordById.set(id, r);
      });

      const built = attendanceRaw
        .map((a) => {
          const rid = Number(a?.organization_ems_batch_class_record_id);
          const rec = rid ? recordById.get(rid) : null;
          const bc = rec?.batch_class || rec?.batchClass || {};
          const ymd =
            toYmdDate(a?.attendance_date) ||
            toYmdDate(rec?.start_time) ||
            toYmdDate(bc?.class_date) ||
            "";
          const topic =
            oneLine(rec?.topic) ||
            oneLine(bc?.topic) ||
            oneLine(a?.topic) ||
            "—";
          const batchLabel =
            rec?.batch?.batch_code ||
            rec?.batch?.batch_name ||
            bc?.batch?.batch_code ||
            "—";
          const status = String(a?.attendance_status || "Not marked").trim();
          const recordStatus = normalizeRecordStatus(rec?.status);
          const cancelReason = oneLine(rec?.cancel_reason);
          return {
            attendance_id: a?.organization_ems_class_attendance_id,
            record_id: rid || null,
            _ymd: ymd,
            _topic: topic,
            _batch: batchLabel,
            _trainer: trainerDisplayName(bc?.trainer),
            attendance_status: status || "Not marked",
            _record_status: recordStatus,
            _cancel_reason: cancelReason,
          };
        })
        .filter((s) => s._ymd)
        .sort((a, b) => String(a._ymd).localeCompare(String(b._ymd)));

      setSessions(built);

      const latest = built[built.length - 1];
      if (latest?._ymd) {
        try {
          setCursor(parseISO(`${latest._ymd}T12:00:00`));
          setSelectedYmd(latest._ymd);
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      console.error(e);
      setErrorText(
        e?.response?.data?.message || "Failed to load student attendance."
      );
    } finally {
      setLoading(false);
    }
  }, [open, studentId, orgId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const rangeDays = useMemo(() => {
    if (viewMode === "week") {
      return eachDayOfInterval({
        start: startOfWeek(cursor),
        end: endOfWeek(cursor),
      });
    }
    return eachDayOfInterval({
      start: startOfWeek(startOfMonth(cursor)),
      end: endOfWeek(endOfMonth(cursor)),
    });
  }, [cursor, viewMode]);

  const visibleRangeLabel = useMemo(() => {
    if (viewMode === "week") {
      return `${format(startOfWeek(cursor), "d MMM")} – ${format(
        endOfWeek(cursor),
        "d MMM yyyy"
      )}`;
    }
    return format(cursor, "MMMM yyyy");
  }, [cursor, viewMode]);

  const sessionsByYmd = useMemo(() => {
    const map = {};
    sessions.forEach((s) => {
      if (!map[s._ymd]) map[s._ymd] = [];
      map[s._ymd].push(s);
    });
    return map;
  }, [sessions]);

  const selectedDaySessions = useMemo(
    () => (selectedYmd ? sessionsByYmd[selectedYmd] || [] : []),
    [selectedYmd, sessionsByYmd]
  );

  const dayPrimaryAttendance = useCallback(
    (ymd) => {
      const list = sessionsByYmd[ymd] || [];
      if (!list.length) return "";
      if (list.some((s) => String(s.attendance_status).toLowerCase() === "absent"))
        return "Absent";
      if (list.every((s) => String(s.attendance_status).toLowerCase() === "present"))
        return "Present";
      const first = String(list[0].attendance_status || "").trim();
      if (first.toLowerCase() === "present") return "Present";
      if (first.toLowerCase() === "absent") return "Absent";
      return "Not marked";
    },
    [sessionsByYmd]
  );

  /** True if any of the student's batches schedule this weekday (within that enrollment window). */
  const isScheduledClassDay = useCallback(
    (day) => {
      if (!batchSchedules.length) return false;
      const name = weekdayName(day);
      const ymd = format(day, "yyyy-MM-dd");
      return batchSchedules.some((sch) => {
        if (!sch.days?.length) return false;
        if (sch.start_ymd && ymd < sch.start_ymd) return false;
        if (sch.end_ymd && ymd > sch.end_ymd) return false;
        return sch.days.some(
          (d) => String(d).toLowerCase() === String(name).toLowerCase()
        );
      });
    },
    [batchSchedules]
  );

  /** Day falls inside at least one batch-student enrollment window (start → end). */
  const isWithinEnrollmentWindow = useCallback(
    (day) => {
      if (!batchSchedules.length) return false;
      const ymd = format(day, "yyyy-MM-dd");
      return batchSchedules.some((sch) => {
        if (sch.start_ymd && ymd < sch.start_ymd) return false;
        if (sch.end_ymd && ymd > sch.end_ymd) return false;
        // Need at least one bound; if neither set, treat as active
        return true;
      });
    },
    [batchSchedules]
  );

  const hasAnyBatchDays = batchSchedules.some((s) => s.days?.length > 0);

  const allSessionsSorted = useMemo(() => {
    return [...sessions].sort((a, b) => {
      const da = String(a?._ymd || "");
      const db = String(b?._ymd || "");
      if (da !== db) return db.localeCompare(da);
      return String(a?._batch || "").localeCompare(String(b?._batch || ""));
    });
  }, [sessions]);

  /** Latest enrollment end date (for subtitle / nav hint). */
  const enrollmentEndLabel = useMemo(() => {
    const ends = batchSchedules.map((s) => s.end_ymd).filter(Boolean).sort();
    if (!ends.length) return "";
    return formatDateLabel(ends[ends.length - 1]);
  }, [batchSchedules]);

  const exportRowsForRange = useCallback(() => {
    const ymds = new Set(
      rangeDays
        .filter((d) => (viewMode === "month" ? isSameMonth(d, cursor) : true))
        .map((d) => format(d, "yyyy-MM-dd"))
    );
    return sessions
      .filter((s) => ymds.has(s._ymd))
      .map((s) => ({
        Date: formatDateLabel(s._ymd),
        Batch: s._batch,
        Topic: s._topic,
        Trainer: s._trainer || "—",
        Attendance: s.attendance_status,
        "Class status": s._record_status || "",
        "Cancel reason": s._cancel_reason || "",
      }));
  }, [rangeDays, viewMode, cursor, sessions]);

  const downloadExcel = async () => {
    setDownloadAnchor(null);
    const rows = exportRowsForRange();
    if (!rows.length) {
      toast.error("No attendance data in this period to download.");
      return;
    }
    try {
      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance");
      XLSX.writeFile(
        wb,
        `student_attendance_${safeFilePart(studentName || studentId)}_${viewMode}_${format(
          cursor,
          "yyyy-MM-dd"
        )}.xlsx`
      );
      toast.success("Excel downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to download Excel.");
    }
  };

  const downloadPdf = async () => {
    setDownloadAnchor(null);
    const rows = exportRowsForRange();
    if (!rows.length) {
      toast.error("No attendance data in this period to download.");
      return;
    }
    try {
      const mod = await import("jspdf");
      const jsPDF = mod.jsPDF || mod.default?.jsPDF || mod.default;
      const doc = new jsPDF({
        unit: "pt",
        format: "a4",
        orientation: "landscape",
      });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 36;
      const usableW = pageW - marginX * 2;
      let y = 36;
      const lineH = 16;
      const fit = (t, w) => {
        const raw = oneLine(t) || "—";
        const lines = doc.splitTextToSize(raw, Math.max(8, w));
        return Array.isArray(lines) && lines.length ? lines[0] : raw;
      };
      const colW = {
        date: 78,
        batch: 90,
        topic: 200,
        trainer: 110,
        attendance: 80,
        status: usableW - (78 + 90 + 200 + 110 + 80),
      };
      const colX = {
        date: marginX,
        batch: marginX + colW.date,
        topic: marginX + colW.date + colW.batch,
        trainer: marginX + colW.date + colW.batch + colW.topic,
        attendance:
          marginX + colW.date + colW.batch + colW.topic + colW.trainer,
        status:
          marginX +
          colW.date +
          colW.batch +
          colW.topic +
          colW.trainer +
          colW.attendance,
      };

      const drawHeader = () => {
        y = 36;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(organizationName || "Organization", marginX, y);
        y += 18;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.text(
          `Student attendance — ${oneLine(studentName) || `Student #${studentId}`}`,
          marginX,
          y
        );
        y += 14;
        doc.setFontSize(9);
        doc.setTextColor(90);
        doc.text(
          `Period: ${visibleRangeLabel}${
            studentCode ? ` · ID: ${studentCode}` : ""
          }`,
          marginX,
          y
        );
        doc.setTextColor(20);
        y += 16;
        doc.setFillColor(240, 240, 240);
        doc.rect(marginX, y - 11, usableW, 18, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.text("Date", colX.date + 2, y);
        doc.text("Batch", colX.batch + 2, y);
        doc.text("Topic", colX.topic + 2, y);
        doc.text("Trainer", colX.trainer + 2, y);
        doc.text("Attendance", colX.attendance + 2, y);
        doc.text("Class status", colX.status + 2, y);
        y += 14;
        doc.setFont("helvetica", "normal");
      };

      drawHeader();
      rows.forEach((r) => {
        if (y > pageH - 28) {
          doc.addPage();
          drawHeader();
        }
        doc.setFontSize(8);
        doc.text(fit(r.Date, colW.date - 6), colX.date + 2, y);
        doc.text(fit(r.Batch, colW.batch - 6), colX.batch + 2, y);
        doc.text(fit(r.Topic, colW.topic - 6), colX.topic + 2, y);
        doc.text(fit(r.Trainer, colW.trainer - 6), colX.trainer + 2, y);
        doc.text(fit(r.Attendance, colW.attendance - 6), colX.attendance + 2, y);
        doc.text(
          fit(
            r["Cancel reason"]
              ? `${r["Class status"]} (${r["Cancel reason"]})`
              : r["Class status"],
            colW.status - 6
          ),
          colX.status + 2,
          y
        );
        y += lineH;
      });

      doc.save(
        `student_attendance_${safeFilePart(studentName || studentId)}_${viewMode}_${format(
          cursor,
          "yyyy-MM-dd"
        )}.pdf`
      );
      toast.success("PDF downloaded.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to download PDF.");
    }
  };

  const renderDayCell = (day) => {
    const ymd = format(day, "yyyy-MM-dd");
    const inMonth = viewMode === "week" || isSameMonth(day, cursor);

    // Month view: keep weekday columns aligned, but hide other-month dates.
    if (viewMode === "month" && !inMonth) {
      return (
        <Box
          key={ymd}
          aria-hidden
          sx={{
            minHeight: embedded ? 72 : 92,
            border: "1px solid transparent",
            bgcolor: "transparent",
            pointerEvents: "none",
          }}
        />
      );
    }

    const list = sessionsByYmd[ymd] || [];
    const isSelected = selectedYmd === ymd;
    const isToday = isSameDay(day, new Date());
    const has = list.length > 0;
    const att = dayPrimaryAttendance(ymd);
    const scheduled = isScheduledClassDay(day);
    const inEnrollment = isWithinEnrollmentWindow(day);
    const showNoClass =
      inMonth &&
      !has &&
      hasAnyBatchDays &&
      inEnrollment &&
      !scheduled;
    const showClassDay =
      inMonth && !has && scheduled;

    let cellBg = "background.paper";
    if (has) {
      cellBg =
        attendanceBgColor(att) ||
        RECORD_STATUS_COLORS[list[0]?._record_status] ||
        "#E3F2FD";
    } else if (showNoClass) {
      cellBg = NO_CLASS_BG;
    } else if (showClassDay) {
      cellBg = CLASS_DAY_EMPTY_BG;
    }

    return (
      <Box
        key={ymd}
        onClick={() => {
          if (has) setSelectedYmd(ymd);
          else setSelectedYmd(null);
        }}
        sx={{
          minHeight: isNarrow
            ? 46
            : embedded
              ? viewMode === "week"
                ? 88
                : 72
              : viewMode === "week"
                ? 120
                : 92,
          border: "1px solid",
          borderColor: isSelected ? "primary.main" : "divider",
          bgcolor: cellBg,
          p: isNarrow ? 0.25 : embedded ? 0.5 : 0.75,
          cursor: has ? "pointer" : "default",
          display: "flex",
          flexDirection: "column",
          alignItems: isNarrow ? "center" : "stretch",
          gap: isNarrow ? 0 : 0.4,
          boxShadow: isSelected
            ? "inset 0 0 0 2px rgba(25,118,210,0.45)"
            : "none",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={isToday ? 700 : 500}
          color={showNoClass ? "text.secondary" : "text.primary"}
          sx={{
            width: isNarrow ? 18 : 22,
            height: isNarrow ? 18 : 22,
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isToday ? "primary.main" : "transparent",
            color: isToday ? "primary.contrastText" : undefined,
            fontSize: isNarrow ? 11 : undefined,
          }}
        >
          {format(day, "d")}
        </Typography>
        {isNarrow ? (
          has ? (
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ fontSize: 10, lineHeight: 1, mt: "auto" }}
            >
              {list.length > 1
                ? `${shortAttendanceMark(att)}${list.length}`
                : shortAttendanceMark(att)}
            </Typography>
          ) : null
        ) : (
          <>
            {has
              ? list.slice(0, 2).map((s, idx) => (
                  <Tooltip
                    key={`${s.attendance_id || s.record_id}-${idx}`}
                    title={`${s._topic} · ${s.attendance_status}${
                      s._cancel_reason ? ` · ${s._cancel_reason}` : ""
                    }`}
                  >
                    <Chip
                      size="small"
                      label={
                        s._topic.length > 22
                          ? `${s._topic.slice(0, 21)}…`
                          : s._topic
                      }
                      sx={{
                        height: "auto",
                        minHeight: 20,
                        maxWidth: "100%",
                        fontSize: 10,
                        "& .MuiChip-label": {
                          whiteSpace: "normal",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          px: 0.75,
                          py: 0.2,
                          lineHeight: 1.2,
                        },
                      }}
                    />
                  </Tooltip>
                ))
              : null}
            {has ? (
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                {att}
              </Typography>
            ) : null}
            {showNoClass ? (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ mt: "auto" }}
              >
                No class
              </Typography>
            ) : null}
            {showClassDay ? (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ mt: "auto" }}
              >
                Class day
              </Typography>
            ) : null}
          </>
        )}
      </Box>
    );
  };

  const subtitle = (
    <>
      {!embedded ? (
        <>
          {studentName || `Student #${studentId}`}
          {studentCode ? ` · ${studentCode}` : ""}
          {(sessions.length || batchDaysLabel || enrollmentEndLabel) ? " · " : ""}
        </>
      ) : null}
      {sessions.length
        ? `${sessions.length} attendance record${
            sessions.length === 1 ? "" : "s"
          }`
        : embedded
          ? "No attendance records yet"
          : ""}
      {batchDaysLabel
        ? ` · Days: ${
            isNarrow && batchDaysLabel.split(",").filter((d) => d.trim()).length >= 7
              ? "every day"
              : isNarrow
                ? batchDaysLabel
                    .split(",")
                    .map((d) => d.trim().slice(0, 3))
                    .filter(Boolean)
                    .join(", ")
                : batchDaysLabel
          }`
        : ""}
      {enrollmentEndLabel ? ` · Until: ${enrollmentEndLabel}` : ""}
    </>
  );

  const toolbar = (
    <Box
      display="flex"
      flexDirection={isNarrow ? "column" : "row"}
      alignItems={isNarrow ? "stretch" : "center"}
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1}
      mb={1.5}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <ButtonGroup size="small" variant="outlined">
          <Button
            variant={panelMode === "calendar" ? "contained" : "outlined"}
            onClick={() => setPanelMode("calendar")}
          >
            Calendar
          </Button>
          <Button
            variant={panelMode === "list" ? "contained" : "outlined"}
            onClick={() => setPanelMode("list")}
          >
            List
          </Button>
        </ButtonGroup>
        {panelMode === "calendar" && !isNarrow ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton
              size="small"
              onClick={() =>
                setCursor((d) =>
                  viewMode === "week" ? subWeeks(d, 1) : subMonths(d, 1)
                )
              }
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              fontWeight={600}
              sx={{ minWidth: 140, textAlign: "center" }}
            >
              {visibleRangeLabel}
            </Typography>
            <IconButton
              size="small"
              onClick={() =>
                setCursor((d) =>
                  viewMode === "week" ? addWeeks(d, 1) : addMonths(d, 1)
                )
              }
            >
              <ChevronRightIcon />
            </IconButton>
            <Button size="small" onClick={() => setCursor(new Date())}>
              Today
            </Button>
            <ButtonGroup size="small" variant="outlined">
              <Button
                variant={viewMode === "month" ? "contained" : "outlined"}
                onClick={() => setViewMode("month")}
              >
                Month
              </Button>
              <Button
                variant={viewMode === "week" ? "contained" : "outlined"}
                onClick={() => setViewMode("week")}
              >
                Week
              </Button>
            </ButtonGroup>
          </Box>
        ) : null}
      </Box>
      {panelMode === "calendar" && isNarrow ? (
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5} flexWrap="wrap">
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              aria-label="Previous"
              onClick={() =>
                setCursor((d) =>
                  viewMode === "week" ? subWeeks(d, 1) : subMonths(d, 1)
                )
              }
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography fontWeight={600} sx={{ minWidth: 120, textAlign: "center" }}>
              {visibleRangeLabel}
            </Typography>
            <IconButton
              size="small"
              aria-label="Next"
              onClick={() =>
                setCursor((d) =>
                  viewMode === "week" ? addWeeks(d, 1) : addMonths(d, 1)
                )
              }
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Button size="small" onClick={() => setCursor(new Date())}>
              Today
            </Button>
            <ButtonGroup size="small" variant="outlined">
              <Button
                variant={viewMode === "month" ? "contained" : "outlined"}
                onClick={() => setViewMode("month")}
              >
                Month
              </Button>
              <Button
                variant={viewMode === "week" ? "contained" : "outlined"}
                onClick={() => setViewMode("week")}
              >
                Week
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      ) : null}
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={(e) => setDownloadAnchor(e.currentTarget)}
          disabled={loading || !!errorText}
        >
          Download
        </Button>
        <Menu
          anchorEl={downloadAnchor}
          open={Boolean(downloadAnchor)}
          onClose={() => setDownloadAnchor(null)}
        >
          <MenuItem onClick={downloadExcel}>Excel (.xlsx)</MenuItem>
          <MenuItem onClick={downloadPdf}>PDF</MenuItem>
        </Menu>
        {!embedded ? (
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );

  const renderSessionCards = (rows, { showDate = false } = {}) => {
    if (!rows.length) {
      return (
        <Typography color="text.secondary" sx={{ py: 1 }}>
          No attendance records found for this student.
        </Typography>
      );
    }
    return (
      <Box display="flex" flexDirection="column" gap={1}>
        {rows.map((s, idx) => (
          <Paper
            key={`${s.attendance_id || s.record_id}-${idx}`}
            variant="outlined"
            sx={{ p: 1.25 }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box sx={{ minWidth: 0 }}>
                {showDate ? (
                  <Typography variant="body2" fontWeight={700}>
                    {formatDateLabel(s._ymd)}
                  </Typography>
                ) : null}
                <Typography variant="body2" fontWeight={showDate ? 500 : 700} sx={{ wordBreak: "break-word" }}>
                  {s._topic || "—"}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={s.attendance_status}
                color={statusChipColor(s.attendance_status)}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {[s._batch, s._trainer].filter(Boolean).join(" · ") || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {s._record_status || "—"}
              {s._cancel_reason ? ` · ${s._cancel_reason}` : ""}
            </Typography>
          </Paper>
        ))}
      </Box>
    );
  };

  const listTable = isNarrow ? (
    renderSessionCards(allSessionsSorted, { showDate: true })
  ) : (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: embedded ? "52vh" : "48vh" }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Batch</TableCell>
            <TableCell>Topic</TableCell>
            <TableCell>Trainer</TableCell>
            <TableCell>Attendance</TableCell>
            <TableCell>Class status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {allSessionsSorted.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No attendance records found for this student.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            allSessionsSorted.map((s, idx) => (
              <TableRow key={`${s.attendance_id || s.record_id}-${idx}`}>
                <TableCell>{formatDateLabel(s._ymd)}</TableCell>
                <TableCell>{s._batch}</TableCell>
                <TableCell>{s._topic}</TableCell>
                <TableCell>{s._trainer || "—"}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={s.attendance_status}
                    color={statusChipColor(s.attendance_status)}
                  />
                </TableCell>
                <TableCell>
                  {s._record_status || "—"}
                  {s._cancel_reason ? ` · ${s._cancel_reason}` : ""}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const calendarBody = (
    <>
      {!sessions.length ? (
        <Typography color="text.secondary" mb={2}>
          No attendance records found for this student.
        </Typography>
      ) : null}

      <Paper variant="outlined" sx={{ overflow: "hidden", mb: 1 }}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(7, 1fr)"
          sx={{
            bgcolor: "action.hover",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {WEEKDAY_LABELS.map((d) => (
            <Box key={d} px={isNarrow ? 0 : 1} py={0.75} textAlign="center">
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {isNarrow ? d.slice(0, 1) : d}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box display="grid" gridTemplateColumns="repeat(7, 1fr)">
          {rangeDays.map((day) => renderDayCell(day))}
        </Box>
      </Paper>

      <Box display="flex" flexWrap="wrap" gap={1} mb={2} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          Attendance:
        </Typography>
        {Object.entries(ATTENDANCE_COLORS).map(([label, color]) => (
          <Box key={label} display="inline-flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: color,
                border: "1px solid",
                borderColor: "divider",
              }}
            />
            <Typography variant="caption">{label}</Typography>
          </Box>
        ))}
        <Box display="inline-flex" alignItems="center" gap={0.5}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.5,
              bgcolor: CLASS_DAY_EMPTY_BG,
              border: "1px solid",
              borderColor: "divider",
            }}
          />
          <Typography variant="caption">Class day</Typography>
        </Box>
        <Box display="inline-flex" alignItems="center" gap={0.5}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 0.5,
              bgcolor: NO_CLASS_BG,
              border: "1px solid",
              borderColor: "divider",
            }}
          />
          <Typography variant="caption">No class</Typography>
        </Box>
      </Box>

      {selectedYmd ? (
        <Box>
          <Typography variant="subtitle1" fontWeight={700} mb={1}>
            {formatDateLabel(selectedYmd)}
          </Typography>
          {selectedDaySessions.length === 0 ? (
            <Typography color="text.secondary">
              No attendance on this day.
            </Typography>
          ) : isNarrow ? (
            renderSessionCards(selectedDaySessions)
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxHeight: embedded ? "28vh" : "36vh" }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Batch</TableCell>
                    <TableCell>Topic</TableCell>
                    <TableCell>Trainer</TableCell>
                    <TableCell>Attendance</TableCell>
                    <TableCell>Class status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedDaySessions.map((s, idx) => (
                    <TableRow
                      key={`${s.attendance_id || s.record_id}-${idx}`}
                    >
                      <TableCell>{s._batch}</TableCell>
                      <TableCell>{s._topic}</TableCell>
                      <TableCell>{s._trainer || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={s.attendance_status}
                          color={statusChipColor(s.attendance_status)}
                        />
                      </TableCell>
                      <TableCell>
                        {s._record_status || "—"}
                        {s._cancel_reason ? ` · ${s._cancel_reason}` : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {isNarrow
            ? "P is present, A is absent. Grey days have no class. Tap a marked day to see the class."
            : `Click a day with attendance to see details. Switch to List for all records. Download exports the current ${viewMode} view.`}
        </Typography>
      )}
    </>
  );

  const mainContent = loading ? (
    <Box display="flex" justifyContent="center" py={6}>
      <CircularProgress size={28} />
    </Box>
  ) : errorText ? (
    <Typography color="error">{errorText}</Typography>
  ) : (
    <>
      {toolbar}
      {panelMode === "list" ? listTable : calendarBody}
    </>
  );

  if (embedded) {
    if (!open) return null;
    const hasMeta =
      sessions.length > 0 || batchDaysLabel || enrollmentEndLabel;
    return (
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {hasMeta ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.25, lineHeight: 1.45 }}
          >
            {subtitle}
          </Typography>
        ) : null}
        {mainContent}
      </Box>
    );
  }

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      fullScreen={isNarrow}
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: isNarrow ? 0 : 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          flexWrap: "wrap",
          pb: 1,
        }}
      >
        <Box>
          <Typography component="span" variant="h6" fontWeight={700}>
            Student attendance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>{mainContent}</DialogContent>
    </Dialog>
  );
}
